import type { Emitter } from "@core/events/emitter";

/**
 * `IBoundingBoxToolModel` — web port of Unity's `IBoundingBoxToolModel`
 * (`Models/BoundingBoxToolModel.cs`), the state owner behind the "Measure"
 * tool's Dimensions mode. Targets are entity-snapshot ids (the web
 * equivalent of the attached `GameObject`).
 *
 * Session-local only — Unity persists nothing and broadcasts nothing for
 * bounding boxes; the only participant-visible state is the active-tool
 * flag (deferred with the `at` publishing work).
 *
 * ⚠ The NAME is narrower than the type: since 2026-09-01 a target may draw a
 * height ruler and no box at all (see {@link MeasureVisual}). Kept because it
 * is the Unity port name, it is the IOC token, and one owner is what makes
 * `clear_bounding_box` and the per-turn agent clear take BOTH visuals down —
 * which is what a second model would have had to duplicate.
 *
 * Semantics preserved exactly:
 *   - at most one transient (hover) box at a time;
 *   - at most one pinned box (pinning destroys every other box);
 *   - pin-clicking the already-pinned target clears ALL boxes;
 *   - `removeBoundingBoxObject(null)` clears all, with an id only clears
 *     the transient box (pinned survives hover-exit).
 */

/**
 * Who asked for the box. Web-first: Unity only ever draws one from the Measure
 * tool, so leaving the tool could clear everything. Spark draws one alongside a
 * dimension answer and that one has to outlive the tool — the answer is on
 * screen, and picking up a different tool is not the user dismissing it.
 */
export type BoundingBoxOrigin = "tool" | "agent";

/**
 * What a target DRAWS. The two are mutually exclusive, and that is the whole
 * shape of this type:
 *
 *   - `box`         — the oriented box and its three size pills. A SIZE answer;
 *                     the box is what shows which span each figure refers to.
 *   - `groundRuler` — a dashed plumb line from the part's lowest MESH point to
 *                     the ground plane, carrying the clearance figure. A HEIGHT
 *                     answer, and it draws NO box.
 *   - `centerlineRuler` — the same plumb line, starting from the part's
 *                     horizontal centreline instead (midway between its lowest
 *                     and highest mesh points). A CENTRELINE-height answer; no
 *                     box either.
 *   - `toplineRuler` — the same plumb line, from the part's HIGHEST mesh point.
 *                     A TOPLINE-height answer (overhead clearance, a maximum
 *                     mounting height); no box.
 *
 * ⚠ The ruler used to be a second flag ON a box, so a height answer arrived
 * with the box around it (2026-09-01, team feedback: "get rid of the bounding
 * box… only draw a ruler"). A box adds nothing to a clearance answer — it shows
 * three spans nobody asked about — so the two became one choice rather than a
 * box plus an optional extra. An enum rather than two booleans because
 * `{ box: true, ruler: true }` is not a state this feature has, and a boolean
 * pair invites it back.
 *
 * A target that draws NOTHING is not represented: it is simply not a target.
 */
export type MeasureVisual = "box" | "groundRuler" | "centerlineRuler" | "toplineRuler";

export interface BoundingBoxTarget {
  readonly snapshotId: string;
  readonly isPersistent: boolean;
  readonly origin: BoundingBoxOrigin;
  /**
   * Which of the two visuals this target draws.
   *
   * DELIBERATELY its own field rather than derived from `origin === "agent"`,
   * which is what the ruler flag was at first. `Get_Part_Dimensions` answers
   * size OR clearance, so who drew the target says nothing about which of the
   * two was ASKED — and a length question answered with a line to the floor
   * illustrates a question the user did not ask. The agent sets this from the
   * question; `origin` goes on meaning ownership, which is what
   * `clearBoundingBoxes` filters on.
   */
  readonly visual: MeasureVisual;
}

export interface IBoundingBoxToolModel {
  readonly onChanged: Emitter<ReadonlyArray<BoundingBoxTarget>>;
  readonly targets: ReadonlyArray<BoundingBoxTarget>;

  addBoundingBoxObject(
    snapshotId: string,
    isPersistant?: boolean,
    origin?: BoundingBoxOrigin,
    visual?: MeasureVisual,
  ): void;
  /**
   * Switch what an ALREADY-pinned target draws, without disturbing the pin.
   *
   * Exists because a conversation moves between the two questions about one
   * part ("how long is it?" then "how high off the ground?"), and they want
   * different visuals. Re-pinning to carry the new one is not an option:
   * `addBoundingBoxObject` reads a pin on an already-pinned target as Unity's
   * pin-click toggle and destroys every target, which is exactly what the
   * executor's guard exists to avoid.
   *
   * No-op when the target is absent or already showing that visual, so it
   * never emits a repaint for nothing.
   */
  setTargetVisual(snapshotId: string, visual: MeasureVisual): void;
  removeBoundingBoxObject(snapshotId?: string | null): void;
  /** Clears one owner's boxes, leaving the other owner's standing. */
  clearBoundingBoxes(origin: BoundingBoxOrigin): void;

  dispose(): void;
}

export const BOUNDING_BOX_TOOL_MODEL = Symbol.for("Campfire.BoundingBoxToolModel");
